import type { ICDAlgoliaOption, InsuranceAlgoliaOption } from "core/types";
/**
 * Helpers for assessment form pickers (Algolia insurance / ICD) — not Zod schemas.
 *
 * Bridges string Algolia option ids to numeric API fields and legacy select shapes
 * used in `getDefaultValues` and RHF field components.
 */
import type { ICDAlgoliaHit, InsuranceAlgoliaHit } from "core/types";

export function mapInsuranceAlgoliaHit(
  hit: InsuranceAlgoliaHit,
): InsuranceAlgoliaOption {
  const value = String(hit.id ?? hit.objectID ?? "");
  return {
    careseekerId: hit.careseeker_id,
    id: value,
    is_active: hit.is_active,
    label: hit.name ?? "",
    value,
  };
}

export function mapICDAlgoliaHit(hit: ICDAlgoliaHit): ICDAlgoliaOption {
  const { code, description, objectID } = hit;
  return {
    code,
    description,
    id: objectID ?? code,
    label: `(${code}) ${description}`,
    value: code,
  };
}

/**
 * Patient / assessment Zod schemas expect numeric insurer ids (`payers.insurance.id`,
 * `patient.profile.financing.insurance_id`, …) while Algolia options use string keys.
 */
export function insuranceAlgoliaIdToFormNumber(
  raw: string | null | undefined,
): number | null {
  if (raw == null || raw === "") {
    return null;
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function toInsuranceAlgoliaOption(
  insurance:
    | { id?: number | string | null; name?: string | null }
    | null
    | undefined,
): InsuranceAlgoliaOption | null {
  if (!insurance) return null;
  const value = insurance.id != null ? String(insurance.id) : "";
  const label = insurance.name ?? "";
  if (!value && !label) return null;
  return {
    id: value || label,
    label,
    value: value || label,
  };
}

export function toIcdCodeAlgoliaOption(
  icdCode:
    | { code?: string | null; description?: string | null }
    | null
    | undefined,
): ICDAlgoliaOption | null {
  if (!icdCode?.code) return null;
  const code = icdCode.code;
  const description = icdCode.description ?? "";
  return {
    code,
    description,
    id: code,
    label: `(${code}) ${description}`,
    value: code,
  };
}

/**
 * Backfill `payers.insurance` from the Algolia picker (`payers._insurance`) when
 * the UI shows a selection but nested `insurance` was never written (e.g.
 * elective-rehab edit defaults).
 */
export function resolvePayersInsuranceFromPickerField(payers: {
  _insurance?: unknown;
  insurance?: { id?: number | null; name?: string | null } | null;
}): void {
  if (payers.insurance?.id != null) {
    return;
  }

  const option = coerceInsuranceAlgoliaOptionFromFormValue(payers._insurance);
  const id = insuranceAlgoliaIdToFormNumber(option?.value ?? null);
  if (id == null) {
    return;
  }

  payers.insurance = { id, name: option?.label ?? "" };
}

/** Normalise a stored RHF value (legacy select option shape) for insurance pickers. */
export function coerceInsuranceAlgoliaOptionFromFormValue(
  raw: unknown,
): InsuranceAlgoliaOption | null {
  if (raw == null || raw === "") {
    return null;
  }
  if (
    typeof raw === "object" &&
    raw !== null &&
    "label" in raw &&
    "value" in raw
  ) {
    const o = raw as {
      careseekerId?: string;
      careseeker_id?: string;
      id?: unknown;
      is_active?: boolean;
      label: string;
      value: unknown;
    };
    const value = String(o.value ?? o.id ?? "");
    if (!value && !o.label) {
      return null;
    }
    return {
      careseekerId: o.careseekerId ?? o.careseeker_id,
      id: value || o.label,
      is_active: o.is_active,
      label: o.label,
      value: value || o.label,
    };
  }
  return null;
}
